import { Button, Col, Dropdown, Input, message, Row, Select, Slider, Space, Tag, theme, Tooltip, Typography } from 'antd';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIntl } from '@umijs/max';
import { CloseOutlined, HighlightOutlined } from '@ant-design/icons';
import { AssetSelector, type AssetItem as SelectorAssetItem, type AssetKind } from '@/components';
import {
  AI_SCENE_IMAGE,
  AI_SCENE_VIDEO_FIRST_LAST,
  AI_SCENE_VIDEO_REF,
  findModel,
  findScene,
  submitBusinessModelCell,
  type AiBusinessModel,
  type AiBusinessModelComponentParameter,
} from '@/services/ai/businessModel';
import { getDefaultWorkspaceId } from '@/utils/memberWorkspaceCache';
import {
  coercePrefillUrlList,
  extractScalarText,
  extractTextValue,
  getParsedEntry,
  isPromptComponentKey,
  isRefImageComponentKey,
  isUrlOnlyPrefillValue,
  parseBusinessParamsJson,
} from '@/utils/businessParamsParse';
import './AIIntegration.less';

type ParamComponentType =
  | 1 // input
  | 2 // select
  | 3 // textarea
  | 4 // image
  | 5 // video
  | 6 // tag
  | 7; // duration

type ParamPresetItem = { label: string; value: string };
type ParamComponentSchema = {
  componentName: string;
  componentKey: string;
  componentType: ParamComponentType;
  componentMinLimit?: number;
  componentMaxLimit?: number;
  componentDefaultValue?: string;
  componentRequired: 0 | 1;
  componentRule?: string;
  componentTips?: string;
  componentPreset?: ParamPresetItem[];
};

export type AIIntegrationPrefill = {
  businessModelName?: string;
  businessParams?: string;
  refMode?: 'ref' | 'firstLast';
};

export type AIIntegrationProps = {
  showRefModeSwitch?: boolean;
  /** 重新创作等场景：回填业务参数到创作区 */
  prefill?: AIIntegrationPrefill | null;
  /** 变更时重新应用 prefill（即使对象内容相同） */
  prefillKey?: number;
  /** 提交生成任务成功后的回调（如跳转创作列表页） */
  onSubmitSuccess?: () => void;
};

function parseComponentPreset(raw?: string): ParamPresetItem[] | undefined {
  if (!raw?.trim()) return undefined;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return undefined;
    return parsed
      .map((item) => {
        if (item && typeof item === 'object' && 'label' in item && 'value' in item) {
          return {
            label: String((item as ParamPresetItem).label),
            value: String((item as ParamPresetItem).value),
          };
        }
        return null;
      })
      .filter((x): x is ParamPresetItem => x != null);
  } catch {
    return undefined;
  }
}

function mapComponentToSchema(c: AiBusinessModelComponentParameter): ParamComponentSchema {
  return {
    componentName: c.componentName,
    componentKey: c.componentKey,
    componentType: c.componentType as ParamComponentType,
    componentMinLimit: c.componentMinLimit,
    componentMaxLimit: c.componentMaxLimit,
    componentDefaultValue: c.componentDefaultValue,
    componentRequired: 0,
    componentRule: c.componentRule,
    componentTips: c.componentTips,
    componentPreset: parseComponentPreset(c.componentPreset),
  };
}

function coerceListValue(v: unknown): string[] {
  if (Array.isArray(v)) return v.filter((x): x is string => typeof x === 'string');
  if (typeof v !== 'string') return [];
  return v
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function isMediaAssetComponent(c: ParamComponentSchema): boolean {
  return c.componentType === 4 || c.componentType === 5;
}

function getMediaAssetKeys(schema: ParamComponentSchema[]): string[] {
  return schema.filter(isMediaAssetComponent).map((c) => c.componentKey);
}

function assetSelectionsToParamFields(
  schema: ParamComponentSchema[],
  selections: Record<string, SelectorAssetItem[]>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const c of schema) {
    if (!isMediaAssetComponent(c)) continue;
    const items = selections[c.componentKey] ?? [];
    out[c.componentKey] = items.map((x) => x.url).join(',');
  }
  return out;
}

function getComposerPromptKey(schema: ParamComponentSchema[]): string | undefined {
  return schema.find((c) => c.componentType === 3)?.componentKey;
}

/** 主编辑区已单独渲染的图片/视频参数，不在参数面板摘要中展示 */
function getComposerAssetKeys(schema: ParamComponentSchema[]): Set<string> {
  const keys = new Set<string>();
  const video = schema.find((c) => c.componentType === 5);
  if (video) keys.add(video.componentKey);
  const multiImage = schema.find(
    (c) => c.componentType === 4 && (c.componentMaxLimit ?? 0) > 1,
  );
  if (multiImage) keys.add(multiImage.componentKey);
  for (const c of schema) {
    if (c.componentType === 4 && (c.componentMaxLimit ?? 1) <= 1) {
      keys.add(c.componentKey);
    }
  }
  return keys;
}

/** 动态参数 JSON：key 为 componentKey，value 为用户输入/选中值 */
function buildBusinessParamsJson(
  schema: ParamComponentSchema[],
  values: Record<string, unknown>,
  promptText: string,
): string {
  const payload: Record<string, unknown> = {};
  const promptKey = getComposerPromptKey(schema);
  for (const c of schema) {
    const raw =
      promptKey && c.componentKey === promptKey ? promptText : values[c.componentKey];
    if (c.componentType === 6) {
      const list = coerceListValue(raw);
      if (list.length > 0) {
        payload[c.componentKey] = list;
      }
      continue;
    }
    if (c.componentType === 4 || c.componentType === 5) {
      const list = coerceListValue(raw);
      if (list.length > 0) {
        payload[c.componentKey] = list;
      }
      continue;
    }
    if (raw === undefined || raw === null || raw === '') continue;
    payload[c.componentKey] = raw;
  }
  return JSON.stringify(payload);
}

function isPanelSpecCandidate(c: ParamComponentSchema, schema: ParamComponentSchema[]): boolean {
  const promptKey = getComposerPromptKey(schema);
  if (promptKey && c.componentType === 3 && c.componentKey === promptKey) return false;
  if (isMediaAssetComponent(c) && getComposerAssetKeys(schema).has(c.componentKey)) return false;
  return true;
}

function formatParamDisplayValue(c: ParamComponentSchema, raw: unknown): string {
  if (c.componentType === 6) {
    const list = coerceListValue(raw);
    const preset = c.componentPreset ?? [];
    const map = new Map(preset.map((p) => [p.value, p.label] as const));
    return list
      .map((v) => map.get(v) ?? v)
      .filter(Boolean)
      .join('/');
  }

  if (c.componentType === 2) {
    const v = String(raw ?? '');
    const preset = c.componentPreset ?? [];
    const hit = preset.find((p) => p.value === v);
    return hit?.label ?? v;
  }

  if (c.componentType === 7) {
    const v = String(raw ?? '');
    return v ? `${v}s` : '';
  }

  return String(raw ?? '').trim();
}

function hasParamSelectedValue(c: ParamComponentSchema, raw: unknown): boolean {
  return Boolean(formatParamDisplayValue(c, raw));
}

function resolveModelCost(cost?: string | null): number {
  if (cost == null || cost === '') return 0;
  const n = Number(cost);
  return Number.isFinite(n) ? n : 0;
}

function isParamValuesEqual(
  a: Record<string, unknown>,
  b: Record<string, unknown>,
): boolean {
  const keys = Object.keys(a);
  if (keys.length !== Object.keys(b).length) return false;
  return keys.every((k) => {
    const va = a[k];
    const vb = b[k];
    if (Array.isArray(va) && Array.isArray(vb)) {
      return va.length === vb.length && va.every((x, i) => x === vb[i]);
    }
    return Object.is(va, vb);
  });
}

type PrefillSession = {
  parsed: Record<string, unknown>;
  businessModelName?: string;
  targetModelId: number | null;
  applied: boolean;
  /** 已完成回填的模型 id，避免用旧 schema 误标记 applied */
  appliedForModelId: number | null;
};

function canApplyPrefill(
  session: PrefillSession,
  modelId: number,
  prefillKey: number,
  lastAppliedKey: number,
): boolean {
  if (session.applied && session.appliedForModelId === modelId && lastAppliedKey === prefillKey) {
    return false;
  }
  if (session.targetModelId != null && session.targetModelId !== modelId) return false;
  return true;
}

function resolveModelIdByName(
  models: AiBusinessModel[],
  businessModelName?: string,
): number | null {
  const name = businessModelName?.trim();
  if (!name) return null;
  return (
    models.find((m) => m.modelName === name)?.id ??
    models.find((m) => name.includes(m.modelName) || m.modelName.includes(name))?.id ??
    null
  );
}

function resolvePresetPrefillValue(c: ParamComponentSchema, text: string): string {
  const preset = c.componentPreset ?? [];
  const hit = preset.find((p) => p.value === text || p.label === text);
  return hit?.value ?? text;
}

function resolveTagPrefillValues(c: ParamComponentSchema, raw: unknown): string[] {
  const items = Array.isArray(raw) ? raw : [raw];
  return items
    .map((item) => extractScalarText(item))
    .filter(Boolean)
    .map((text) => resolvePresetPrefillValue(c, text));
}

function normalizePrefillComponentValue(
  c: ParamComponentSchema,
  raw: unknown,
): unknown | undefined {
  if (raw === undefined || raw === null || raw === '') return undefined;

  if (c.componentType === 6) {
    const list = resolveTagPrefillValues(c, raw);
    return list.length > 0 ? list : undefined;
  }

  if (c.componentType === 4 || c.componentType === 5) {
    return raw;
  }

  const text = extractTextValue(raw);
  if (!text) return undefined;
  if (c.componentType === 2) return resolvePresetPrefillValue(c, text);
  if (c.componentType === 7) return text.replace(/s$/i, '');
  return text;
}

function urlsToAssetItems(
  c: ParamComponentSchema,
  urls: string[],
): SelectorAssetItem[] {
  return urls.map((url, index) => ({
    id: `prefill-${c.componentKey}-${index}`,
    url,
    assetType: c.componentType === 5 ? 3 : 2,
  }));
}

function applyPrefillToComposer(
  schema: ParamComponentSchema[],
  parsed: Record<string, unknown>,
): {
  prompt: string;
  paramValues: Record<string, unknown>;
  assetSelections: Record<string, SelectorAssetItem[]>;
} {
  const promptKey = getComposerPromptKey(schema);
  const mediaKeys = new Set(getComposerAssetKeys(schema));
  const consumedParsedKeys = new Set<string>();
  let prompt = '';
  const paramValues: Record<string, unknown> = {};
  const assetSelections: Record<string, SelectorAssetItem[]> = {};

  for (const c of schema) {
    const raw = getParsedEntry(parsed, c.componentKey);
    if (raw === undefined || raw === null || raw === '') continue;
    for (const k of Object.keys(parsed)) {
      if (k.trim().toLowerCase() === c.componentKey.trim().toLowerCase()) {
        consumedParsedKeys.add(k);
      }
    }

    if (promptKey && c.componentKey === promptKey) {
      const text = extractTextValue(raw);
      if (text) prompt = text;
      continue;
    }

    if (mediaKeys.has(c.componentKey) || c.componentType === 4 || c.componentType === 5) {
      const urls = coercePrefillUrlList(raw);
      if (urls.length === 0) continue;
      assetSelections[c.componentKey] = urlsToAssetItems(c, urls);
      continue;
    }

    const normalized = normalizePrefillComponentValue(c, raw);
    if (normalized !== undefined) {
      paramValues[c.componentKey] = normalized;
    }
  }

  const orphanImageUrls: string[] = [];
  for (const [key, raw] of Object.entries(parsed)) {
    if (consumedParsedKeys.has(key)) continue;
    if (isPromptComponentKey(key)) {
      const text = extractTextValue(raw);
      if (text && !/^https?:\/\//i.test(text) && text.length > prompt.length) {
        prompt = text;
      }
      continue;
    }
    if (!isRefImageComponentKey(key) && !isUrlOnlyPrefillValue(raw)) continue;
    for (const url of coercePrefillUrlList(raw)) {
      if (!orphanImageUrls.includes(url)) orphanImageUrls.push(url);
    }
  }

  if (orphanImageUrls.length > 0) {
    const imageSlots = schema.filter((c) => c.componentType === 4 && mediaKeys.has(c.componentKey));
    const multiSlot = imageSlots.find((c) => (c.componentMaxLimit ?? 0) > 1);
    const targetSlot = multiSlot ?? imageSlots[0];
    if (targetSlot) {
      const existing = assetSelections[targetSlot.componentKey] ?? [];
      if (existing.length === 0) {
        assetSelections[targetSlot.componentKey] = urlsToAssetItems(targetSlot, orphanImageUrls);
      } else {
        const known = new Set(existing.map((a) => a.url));
        const merged = [...existing];
        for (const url of orphanImageUrls) {
          if (!known.has(url)) {
            merged.push({
              id: `prefill-${targetSlot.componentKey}-${merged.length}`,
              url,
              assetType: 2,
            });
          }
        }
        assetSelections[targetSlot.componentKey] = merged;
      }
    }
  }

  return { prompt, paramValues, assetSelections };
}

export const AIIntegration: React.FC<AIIntegrationProps> = ({
  showRefModeSwitch = true,
  prefill,
  prefillKey = 0,
  onSubmitSuccess,
}) => {
  const intl = useIntl();
  const { token } = theme.useToken();
  const prefillSessionRef = useRef<PrefillSession | null>(null);
  /** 当前 paramSchema 对应的模型 id；与 selectedModelId 不一致时不允许回填 */
  const schemaModelIdRef = useRef<number | null>(null);
  const skipAssetResetRef = useRef(false);
  const prefillJustAppliedRef = useRef(false);
  const lastAppliedPrefillKeyRef = useRef(-1);
  const prefillKeyRef = useRef(prefillKey);
  prefillKeyRef.current = prefillKey;
  const applyPrefillSessionRef = useRef<
    (schema: ParamComponentSchema[], modelId: number) => void
  >(() => {});

  const [refMode, setRefMode] = useState<'ref' | 'firstLast'>('ref');

  /** 视频参考=1，视频首尾帧=2，图片生成=3 */
  const activeSceneId = useMemo(() => {
    if (!showRefModeSwitch) return AI_SCENE_IMAGE;
    return refMode === 'firstLast' ? AI_SCENE_VIDEO_FIRST_LAST : AI_SCENE_VIDEO_REF;
  }, [showRefModeSwitch, refMode]);
  const [prompt, setPrompt] = useState('');
  const [refModeHover, setRefModeHover] = useState<'ref' | 'firstLast' | null>(null);
  const assetSelectorAnchorRef = useRef<HTMLDivElement | null>(null);
  const [assetSelectorOpen, setAssetSelectorOpen] = useState(false);
  const [assetSelectorKind, setAssetSelectorKind] = useState<AssetKind>('image');
  const [assetSelectorTarget, setAssetSelectorTarget] = useState('');
  const [assetSelectorPlacement, setAssetSelectorPlacement] = useState<'bottomLeft' | 'topLeft'>(
    'bottomLeft',
  );

  // outside-click 关闭拦截已统一放到 AssetSelector 内部管理

  useEffect(() => {
    if (!assetSelectorOpen) return;
    const el = assetSelectorAnchorRef.current;
    if (!el) return;

    const update = () => {
      const rect = el.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      const need = 420; // 资产选择器面板约高度，空间不足时向上展开
      const next: 'bottomLeft' | 'topLeft' =
        spaceBelow < need && spaceAbove > spaceBelow ? 'topLeft' : 'bottomLeft';
      setAssetSelectorPlacement(next);
    };

    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [assetSelectorOpen]);

  const [models, setModels] = useState<AiBusinessModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [selectedModelId, setSelectedModelId] = useState<number | null>(null);
  const selectedModel =
    models.find((m) => m.id === selectedModelId) ?? (models.length > 0 ? models[0] : undefined);

  const [modelDropdownOpen, setModelDropdownOpen] = useState(false);
  const [paramPanelOpen, setParamPanelOpen] = useState(false);
  const [paramLoading, setParamLoading] = useState(false);
  const [paramSchema, setParamSchema] = useState<ParamComponentSchema[]>([]);
  const [paramValues, setParamValues] = useState<Record<string, unknown>>({});
  const [generateCount, setGenerateCount] = useState<1 | 2 | 3 | 4 | 5 | 6>(1);
  const [generateCountOpen, setGenerateCountOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [assetListHoverKey, setAssetListHoverKey] = useState<string | null>(null);
  const [assetSelections, setAssetSelections] = useState<Record<string, SelectorAssetItem[]>>({});

  const composerVideoKey = useMemo(
    () => paramSchema.find((c) => c.componentType === 5)?.componentKey,
    [paramSchema],
  );

  const multiImageKey = useMemo(
    () =>
      paramSchema.find((c) => c.componentType === 4 && (c.componentMaxLimit ?? 0) > 1)?.componentKey,
    [paramSchema],
  );

  const frameAssetKeys = useMemo(
    () =>
      paramSchema
        .filter((c) => c.componentType === 4 && (c.componentMaxLimit ?? 1) <= 1)
        .map((c) => c.componentKey),
    [paramSchema],
  );
  const firstFrameKey = frameAssetKeys[0];
  const lastFrameKey = frameAssetKeys[1];

  const assetSelectorSelectedIds = useMemo(
    () => (assetSelections[assetSelectorTarget] ?? []).map((a) => a.id),
    [assetSelectorTarget, assetSelections],
  );

  const openAssetSelector = (kind: AssetKind, targetKey?: string) => {
    const key =
      targetKey ??
      (kind === 'video'
        ? composerVideoKey
        : multiImageKey ?? paramSchema.find((c) => c.componentType === 4)?.componentKey);
    if (!key) return;
    setAssetSelectorKind(kind);
    setAssetSelectorTarget(key);
    setAssetSelectorOpen(true);
  };

  const toggleSelectFromSelector = (a: SelectorAssetItem, selected: boolean) => {
    const targetSchema = paramSchema.find((c) => c.componentKey === assetSelectorTarget);
    if (!targetSchema || !isMediaAssetComponent(targetSchema)) return;

    const requiredAssetType = targetSchema.componentType === 5 ? 3 : 2;
    if (a.assetType !== requiredAssetType) return;

    const maxLimit = targetSchema.componentMaxLimit;
    const isSingle = (maxLimit ?? 1) <= 1;
    const maxN =
      typeof maxLimit === 'number' && maxLimit > 0
        ? maxLimit
        : isSingle
          ? 1
          : Number.POSITIVE_INFINITY;

    setAssetSelections((prev) => {
      const cur = prev[assetSelectorTarget] ?? [];
      const exists = cur.some((x) => x.id === a.id);
      if (selected) {
        if (exists) return prev;
        if (isSingle) return { ...prev, [assetSelectorTarget]: [a] };
        if (cur.length >= maxN) return prev;
        return { ...prev, [assetSelectorTarget]: [...cur, a] };
      }
      if (!exists) return prev;
      return { ...prev, [assetSelectorTarget]: cur.filter((x) => x.id !== a.id) };
    });
  };

  useEffect(() => {
    if (skipAssetResetRef.current) {
      skipAssetResetRef.current = false;
      return;
    }
    const session = prefillSessionRef.current;
    if (session && !session.applied) return;
    setAssetSelections((prev) => (Object.keys(prev).length === 0 ? prev : {}));
  }, [selectedModelId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setModelsLoading(true);
      try {
        const list = await findScene(activeSceneId);
        if (cancelled) return;
        setModels(list);
        const session = prefillSessionRef.current;
        if (session) {
          const targetId =
            resolveModelIdByName(list, session.businessModelName) ?? list[0]?.id ?? null;
          session.targetModelId = targetId;
          const wasApplied = session.applied;
          if (wasApplied) {
            session.applied = false;
            session.appliedForModelId = null;
            lastAppliedPrefillKeyRef.current = -1;
            setPrompt('');
            setParamValues({});
            setAssetSelections({});
          }
          if (targetId != null) {
            if (!wasApplied) skipAssetResetRef.current = true;
            setSelectedModelId(targetId);
          } else {
            setSelectedModelId(null);
          }
        } else {
          setPrompt('');
          setParamValues({});
          setAssetSelections({});
          setSelectedModelId(list[0]?.id ?? null);
        }
      } catch {
        if (!cancelled) {
          setModels([]);
          setSelectedModelId(null);
        }
      } finally {
        if (!cancelled) setModelsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeSceneId]);

  const initialParamValues = useMemo(() => {
    const v: Record<string, unknown> = {};
    for (const c of paramSchema) {
      if (c.componentType === 6) {
        const def = c.componentDefaultValue;
        v[c.componentKey] = def ? [def] : [];
      } else {
        v[c.componentKey] = c.componentDefaultValue ?? '';
      }
    }
    return v;
  }, [paramSchema]);

  const applyPrefillSession = useCallback((schema: ParamComponentSchema[], modelId: number) => {
    const session = prefillSessionRef.current;
    if (!session || schema.length === 0) return;

    const applied = applyPrefillToComposer(schema, session.parsed);
    skipAssetResetRef.current = true;

    if (applied.prompt) setPrompt(applied.prompt);

    const mediaAssetKeys = new Set(getMediaAssetKeys(schema));
    const assetFields = assetSelectionsToParamFields(schema, applied.assetSelections);
    const nextValues: Record<string, unknown> = {};
    for (const c of schema) {
      if (c.componentType === 6) {
        const def = c.componentDefaultValue;
        nextValues[c.componentKey] = def ? [def] : [];
      } else {
        nextValues[c.componentKey] = c.componentDefaultValue ?? '';
      }
    }
    Object.assign(nextValues, assetFields);
    for (const [k, v] of Object.entries(applied.paramValues)) {
      if (!mediaAssetKeys.has(k)) nextValues[k] = v;
    }
    setAssetSelections(applied.assetSelections);
    setParamValues(nextValues);
    session.applied = true;
    session.appliedForModelId = modelId;
    prefillJustAppliedRef.current = true;
  }, []);

  const tryCompletePrefill = useCallback(() => {
    const session = prefillSessionRef.current;
    if (!session || paramLoading || selectedModelId == null) return;
    if (schemaModelIdRef.current !== selectedModelId) return;
    if (paramSchema.length === 0) return;

    const key = prefillKeyRef.current;
    if (!canApplyPrefill(session, selectedModelId, key, lastAppliedPrefillKeyRef.current)) {
      return;
    }

    if (session.businessModelName && session.targetModelId == null) {
      session.targetModelId = selectedModelId;
    }

    applyPrefillSession(paramSchema, selectedModelId);
    lastAppliedPrefillKeyRef.current = key;
  }, [paramLoading, paramSchema, selectedModelId, applyPrefillSession]);

  applyPrefillSessionRef.current = applyPrefillSession;

  useEffect(() => {
    if (!prefill?.businessParams?.trim()) {
      prefillSessionRef.current = null;
      return;
    }
    const params = parseBusinessParamsJson(prefill.businessParams);
    if (!params) {
      prefillSessionRef.current = null;
      return;
    }
    prefillSessionRef.current = {
      parsed: params,
      businessModelName: prefill.businessModelName,
      targetModelId: null,
      applied: false,
      appliedForModelId: null,
    };
    lastAppliedPrefillKeyRef.current = -1;
    if (prefill.refMode) {
      setRefMode(prefill.refMode);
    } else if (showRefModeSwitch) {
      const hasFrame = Object.keys(params).some(
        (key) =>
          (/bpfirstframe|first_?frame|首帧/i.test(key) ||
            /bplastframe|last_?frame|尾帧/i.test(key)) &&
          coercePrefillUrlList(params[key]).length > 0,
      );
      if (hasFrame) setRefMode('firstLast');
    }
  }, [prefill, prefillKey, showRefModeSwitch]);

  useEffect(() => {
    const session = prefillSessionRef.current;
    if (!session || session.applied || models.length === 0 || modelsLoading) return;
    if (session.targetModelId != null) return;

    const targetId = resolveModelIdByName(models, session.businessModelName);
    if (targetId != null) {
      session.targetModelId = targetId;
      if (targetId !== selectedModelId) {
        skipAssetResetRef.current = true;
        setSelectedModelId(targetId);
      }
    } else if (selectedModelId != null) {
      session.targetModelId = selectedModelId;
    }
  }, [models, modelsLoading, prefillKey, selectedModelId]);

  /** 须在 prefill session 的 useEffect 之后执行（勿用 useLayoutEffect，否则会早于 session 初始化） */
  useEffect(() => {
    tryCompletePrefill();
  }, [prefillKey, prefill, paramLoading, selectedModelId, paramSchema, tryCompletePrefill]);

  const paramPanelSpecSummary = useMemo(() => {
    const candidates = paramSchema.filter((c) => isPanelSpecCandidate(c, paramSchema));
    const toLabel = (c: ParamComponentSchema) =>
      formatParamDisplayValue(c, paramValues[c.componentKey] ?? c.componentDefaultValue ?? '');

    const required = candidates.filter((c) => c.componentRequired === 1).slice(0, 3);
    const source =
      required.length > 0
        ? required
        : candidates
            .filter((c) =>
              hasParamSelectedValue(c, paramValues[c.componentKey] ?? c.componentDefaultValue ?? ''),
            )
            .slice(0, 3);

    return source.map(toLabel).filter(Boolean).slice(0, 3).join(' · ');
  }, [paramSchema, paramValues]);

  const bpPromptTips = useMemo(() => {
    const promptKey = getComposerPromptKey(paramSchema);
    if (!promptKey) return undefined;
    return paramSchema.find((c) => c.componentKey === promptKey)?.componentTips;
  }, [paramSchema]);

  const firstFrameName = useMemo(() => {
    if (!firstFrameKey) return undefined;
    return paramSchema.find((c) => c.componentKey === firstFrameKey)?.componentName;
  }, [paramSchema, firstFrameKey]);

  const lastFrameName = useMemo(() => {
    if (!lastFrameKey) return undefined;
    return paramSchema.find((c) => c.componentKey === lastFrameKey)?.componentName;
  }, [paramSchema, lastFrameKey]);

  useEffect(() => {
    if (selectedModelId == null) {
      schemaModelIdRef.current = null;
      setParamSchema([]);
      return;
    }
    let cancelled = false;
    schemaModelIdRef.current = null;
    const pendingSession = prefillSessionRef.current;
    if (
      pendingSession &&
      (pendingSession.targetModelId === selectedModelId || pendingSession.targetModelId == null) &&
      (!pendingSession.applied || pendingSession.appliedForModelId !== selectedModelId)
    ) {
      pendingSession.applied = false;
      pendingSession.appliedForModelId = null;
      lastAppliedPrefillKeyRef.current = -1;
    }
    (async () => {
      setParamLoading(true);
      try {
        const list = await findModel(selectedModelId);
        if (cancelled) return;
        const schema = list.map(mapComponentToSchema);
        schemaModelIdRef.current = selectedModelId;

        const session = prefillSessionRef.current;
        const key = prefillKeyRef.current;
        if (
          session &&
          schema.length > 0 &&
          canApplyPrefill(session, selectedModelId, key, lastAppliedPrefillKeyRef.current)
        ) {
          if (session.targetModelId == null) {
            session.targetModelId = selectedModelId;
          }
          applyPrefillSessionRef.current(schema, selectedModelId);
          lastAppliedPrefillKeyRef.current = key;
        }

        setParamSchema(schema);
      } catch {
        if (!cancelled) {
          schemaModelIdRef.current = null;
          setParamSchema([]);
        }
      } finally {
        if (!cancelled) setParamLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedModelId]);

  useEffect(() => {
    if (prefillJustAppliedRef.current) {
      prefillJustAppliedRef.current = false;
      return;
    }
    const session = prefillSessionRef.current;
    if (session && !session.applied) return;

    const mediaAssetKeys = new Set(getMediaAssetKeys(paramSchema));
    const assetFields = assetSelectionsToParamFields(paramSchema, assetSelections);
    setParamValues((prev) => {
      const next: Record<string, unknown> = { ...initialParamValues, ...assetFields };
      for (const [k, v] of Object.entries(prev)) {
        if (mediaAssetKeys.has(k) || !(k in initialParamValues)) {
          continue;
        }
        if (v !== '' && v !== initialParamValues[k]) {
          next[k] = v;
        }
      }
      return isParamValuesEqual(prev, next) ? prev : next;
    });
  }, [initialParamValues, paramSchema, assetSelections]);

  useEffect(() => {
    if (prefillJustAppliedRef.current) {
      return;
    }
    const session = prefillSessionRef.current;
    if (session && !session.applied) return;

    const assetFields = assetSelectionsToParamFields(paramSchema, assetSelections);
    setParamValues((prev) => {
      const next = { ...prev, ...assetFields };
      return isParamValuesEqual(prev, next) ? prev : next;
    });
  }, [assetSelections, paramSchema, selectedModelId]);

  const handleSubmitCell = async () => {
    const workspaceId = getDefaultWorkspaceId();
    if (workspaceId == null) {
      message.warning(intl.formatMessage({ id: 'ai.integration.submitNoWorkspace' }));
      return;
    }
    const businessModelId = selectedModelId ?? models[0]?.id;
    if (businessModelId == null || businessModelId <= 0) {
      message.warning(intl.formatMessage({ id: 'ai.integration.submitNoModel' }));
      return;
    }

    setSubmitting(true);
    try {
      const businessParams = buildBusinessParamsJson(paramSchema, paramValues, prompt);
      await submitBusinessModelCell({
        workspaceId,
        businessModelId,
        businessParams,
        generationNumber: generateCount,
      });
      message.success(intl.formatMessage({ id: 'ai.integration.submitSuccess' }));
      onSubmitSuccess?.();
    } catch (error: unknown) {
      const errMsg =
        error instanceof Error
          ? error.message
          : intl.formatMessage({ id: 'ai.integration.submitFailed' });
      message.error(errMsg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <Row gutter={[16, 24]} style={{ marginTop: '1.5rem' }}>
        <Col span={24}>
          <div
            ref={assetSelectorAnchorRef}
            style={{
              maxWidth: 980,
              marginInline: 'auto',
              position: 'relative',
            }}
          >
            <AssetSelector
              kind={assetSelectorKind}
              open={assetSelectorOpen}
              onOpenChange={(o) => {
                setAssetSelectorOpen(o);
              }}
              popupContainer={assetSelectorAnchorRef.current}
              width="trigger"
              placement={assetSelectorPlacement}
              selectedIds={assetSelectorSelectedIds}
              onToggleSelect={toggleSelectFromSelector}
              ignoreOutsideCloseSelector='[data-asset-preview-delete="1"]'
              triggerStyle={{
                position: 'absolute',
                left: 0,
                ...(assetSelectorPlacement === 'topLeft' ? { top: 0 } : { bottom: 0 }),
                width: '100%',
                height: 1,
                pointerEvents: 'none',
              }}
            >
              <span aria-hidden />
            </AssetSelector>

            <div
              style={{
                padding: '1rem',
                borderRadius: 20,
                background: token.colorBgContainer,
                border: `1px solid ${token.colorBorderSecondary}`,
                boxShadow: token.boxShadowSecondary,
              }}
            >
          {showRefModeSwitch ? (
            <div style={{ display: 'flex' }}>
              <div
                style={{
                  display: 'inline-flex',
                  padding: '0.125rem',
                  background: token.colorFillTertiary,
                  borderRadius: 9999,
                  gap: '0.5rem',
                  marginBottom: '.75rem',
                }}
              >
                <Button
                  size="small"
                  type={refMode === 'ref' ? 'primary' : 'text'}
                  onClick={() => setRefMode('ref')}
                  onMouseEnter={() => setRefModeHover('ref')}
                  onMouseLeave={() => setRefModeHover(null)}
                  icon={
                    <span
                      style={{
                        width: '1rem',
                        height: '1rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        verticalAlign: 'middle',
                        color: token.colorText,
                      }}
                      aria-hidden
                    >
                      <i
                        className="iconfont icon-Images"
                        style={{ fontSize: '1rem', lineHeight: 1 }}
                        aria-hidden
                      />
                    </span>
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: '0.75rem',
                    lineHeight: '1rem',
                    fontWeight: 500,
                    paddingLeft: '0.75rem',
                    paddingRight: '0.75rem',
                    borderRadius: 9999,
                    backgroundColor: refMode === 'ref' ? token.colorBgLayout : 'transparent',
                    whiteSpace: 'nowrap',
                    color:
                      refMode === 'ref' || refModeHover === 'ref'
                        ? token.colorText
                        : `color-mix(in srgb, ${token.colorText} 70%, ${token.colorBgContainer} 30%)`,
                    gap: '0.375rem',
                    height: '1.75rem',
                  }}
                >
                  {intl.formatMessage({ id: 'ai.integration.ref' })}
                </Button>
                <Button
                  size="small"
                  type={refMode === 'firstLast' ? 'primary' : 'text'}
                  onClick={() => setRefMode('firstLast')}
                  icon={
                    <span
                      style={{
                        width: '1rem',
                        height: '1rem',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        lineHeight: 1,
                        verticalAlign: 'middle',
                        color: token.colorText,
                      }}
                      aria-hidden
                    >
                      <i
                        className="iconfont icon-film"
                        style={{ fontSize: '1rem', lineHeight: 1 }}
                        aria-hidden
                      />
                    </span>
                  }
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    fontSize: '0.75rem',
                    lineHeight: '1rem',
                    paddingInline: 14,
                    fontWeight: 500,
                    paddingTop: '0.25rem',
                    paddingBottom: '0.25rem',
                    borderRadius: 9999,
                    backgroundColor: refMode === 'firstLast' ? token.colorBgLayout : 'transparent',
                    whiteSpace: 'nowrap',
                    color: refMode === 'firstLast' ? token.colorText : `color-mix(in srgb, ${token.colorText} 70%, ${token.colorBgContainer} 30%)`,
                    gap: '0.375rem',
                    height: '1.75rem',
                  }}
                >
                  {intl.formatMessage({ id: 'ai.integration.firstLastFrame' })}
                </Button>
              </div>
            </div>
          ) : null}
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
            <div style={{ display: 'flex', gap: 10, flex: 1, minWidth: 0 }}>
              {/* 图片组件：有图时预览；>2 张默认收起，仅展示 1 张 + 数字，悬停展开 */}
              {multiImageKey ? (() => {
                const imageSchema = paramSchema.find((c) => c.componentKey === multiImageKey);
                const maxLimit = imageSchema?.componentMaxLimit;
                const maxN =
                  typeof maxLimit === 'number' && maxLimit > 0
                    ? maxLimit
                    : Number.POSITIVE_INFINITY;
                const bpImages = assetSelections[multiImageKey] ?? [];
                const expanded = assetListHoverKey === multiImageKey || bpImages.length <= 2;
                const shouldHideAdd = bpImages.length >= maxN;

                const removeBpImageAt = (removeIdx: number) => {
                  setAssetSelections((prev) => ({
                    ...prev,
                    [multiImageKey]: (prev[multiImageKey] ?? []).filter((_, i) => i !== removeIdx),
                  }));
                };

                return (
                  <div
                    onMouseEnter={() => setAssetListHoverKey(multiImageKey)}
                    onMouseLeave={() => setAssetListHoverKey(null)}
                    style={{
                      display: 'flex',
                      gap: bpImages.length > 0 ? 8 : 0,
                      alignItems: 'center',
                      height: '3.5rem',
                      flexShrink: 0,
                    }}
                  >
                    {/* 图片轨道：展开推右侧 Button，收起从右往左隐藏 */}
                    <div
                      style={{
                        height: '3.5rem',
                        width: expanded
                          ? `${bpImages.length * 40 + Math.max(0, bpImages.length - 1) * 8}px`
                          : '2.5rem',
                        transition: 'width 160ms ease',
                        overflow: 'hidden',
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      {(expanded ? bpImages : bpImages.slice(0, 1)).map((a, idx) => (
                        <div
                          key={`${a.id}-${idx}`}
                          style={{
                            width: '2.5rem',
                            height: '3.5rem',
                            position: 'relative',
                            overflow: 'visible',
                          }}
                        >
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: 12,
                              overflow: 'hidden',
                              background: token.colorFillQuaternary,
                              border: `1px solid ${token.colorBorderSecondary}`,
                            }}
                          >
                            <img
                              src={a.url}
                              alt=""
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                              draggable={false}
                              referrerPolicy="no-referrer"
                            />
                          </div>

                          {!expanded && bpImages.length > 2 && idx === 0 ? (
                            <div
                              style={{
                                position: 'absolute',
                                right: 0,
                                bottom: 0,
                                padding: '2px 6px',
                                borderRadius: 999,
                                background: 'rgba(0,0,0,0.55)',
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '0.625rem',
                                lineHeight: 1,
                                userSelect: 'none',
                                pointerEvents: 'none',
                              }}
                            >
                              {bpImages.length}
                            </div>
                          ) : null}

                          <span
                            role="button"
                            tabIndex={0}
                            data-asset-preview-delete="1"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeBpImageAt(expanded ? idx : 0);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                removeBpImageAt(expanded ? idx : 0);
                              }
                            }}
                            style={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              width: 18,
                              height: 18,
                              borderRadius: 999,
                              background: 'rgba(0,0,0,0.55)',
                              color: 'rgba(255,255,255,0.9)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            aria-label="delete"
                          >
                            <CloseOutlined style={{ fontSize: 10, lineHeight: 1 }} />
                          </span>
                        </div>
                      ))}
                    </div>

                    {!shouldHideAdd ? (
                      <Button
                        type="text"
                        onClick={() => openAssetSelector('image')}
                        icon={
                          <span
                            style={{
                              width: '1.25rem',
                              height: '1.25rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1,
                              color: token.colorText,
                            }}
                            aria-hidden
                          >
                            <i
                              className="iconfont icon-image-plus"
                              style={{ fontSize: '1.25rem', lineHeight: 1 }}
                              aria-hidden
                            />
                          </span>
                        }
                        style={{
                          width: '2.5rem',
                          height: '3.5rem',
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 8,
                          border: `1px dashed ${token.colorBorder}`,
                          background: token.colorFillQuaternary,
                        }}
                      />
                    ) : null}
                  </div>
                );
              })() : null}

              {/* 视频组件：展示逻辑与图片一致 */}
              {composerVideoKey ? (() => {
                const videoSchema = paramSchema.find((c) => c.componentKey === composerVideoKey);
                const maxLimit = videoSchema?.componentMaxLimit;
                const maxN =
                  typeof maxLimit === 'number' && maxLimit > 0
                    ? maxLimit
                    : Number.POSITIVE_INFINITY;
                const bpVideos = assetSelections[composerVideoKey] ?? [];
                const expanded = assetListHoverKey === composerVideoKey || bpVideos.length <= 2;
                const shouldHideAdd = bpVideos.length >= maxN;

                const removeBpVideoAt = (removeIdx: number) => {
                  setAssetSelections((prev) => ({
                    ...prev,
                    [composerVideoKey]: (prev[composerVideoKey] ?? []).filter(
                      (_, i) => i !== removeIdx,
                    ),
                  }));
                };

                if (bpVideos.length === 0) {
                  return (
                    <Button
                      type="text"
                      onClick={() => openAssetSelector('video')}
                      icon={
                        <span
                          style={{
                            width: '1.25rem',
                            height: '1.25rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            lineHeight: 1,
                            color: token.colorText,
                          }}
                          aria-hidden
                        >
                          <i
                            className="iconfont icon-video"
                            style={{ fontSize: '1.25rem', lineHeight: 1 }}
                            aria-hidden
                          />
                        </span>
                      }
                      style={{
                        width: '2.5rem',
                        height: '3.5rem',
                        padding: 0,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 8,
                        border: `1px dashed ${token.colorBorder}`,
                        background: token.colorFillQuaternary,
                      }}
                    />
                  );
                }

                return (
                  <div
                    onMouseEnter={() => setAssetListHoverKey(composerVideoKey)}
                    onMouseLeave={() => setAssetListHoverKey(null)}
                    style={{
                      display: 'flex',
                      gap: 8,
                      alignItems: 'center',
                      height: '3.5rem',
                      flexShrink: 0,
                    }}
                  >
                    <div
                      style={{
                        height: '3.5rem',
                        width: expanded
                          ? `${bpVideos.length * 40 + Math.max(0, bpVideos.length - 1) * 8}px`
                          : '2.5rem',
                        transition: 'width 160ms ease',
                        overflow: 'hidden',
                        display: 'flex',
                        gap: 8,
                        alignItems: 'center',
                      }}
                    >
                      {(expanded ? bpVideos : bpVideos.slice(0, 1)).map((a, idx) => (
                        <div
                          key={`${a.id}-${idx}`}
                          style={{
                            width: '2.5rem',
                            height: '3.5rem',
                            position: 'relative',
                            overflow: 'visible',
                          }}
                        >
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              borderRadius: 12,
                              overflow: 'hidden',
                              background: token.colorFillQuaternary,
                              border: `1px solid ${token.colorBorderSecondary}`,
                            }}
                          >
                            <video
                              src={a.url}
                              muted
                              playsInline
                              preload="metadata"
                              referrerPolicy="no-referrer"
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                          </div>

                          {!expanded && bpVideos.length > 2 && idx === 0 ? (
                            <div
                              style={{
                                position: 'absolute',
                                right: 0,
                                bottom: 0,
                                padding: '2px 6px',
                                borderRadius: 999,
                                background: 'rgba(0,0,0,0.55)',
                                color: 'rgba(255,255,255,0.9)',
                                fontSize: '0.625rem',
                                lineHeight: 1,
                                userSelect: 'none',
                                pointerEvents: 'none',
                              }}
                            >
                              {bpVideos.length}
                            </div>
                          ) : null}

                          <span
                            role="button"
                            tabIndex={0}
                            data-asset-preview-delete="1"
                            onMouseDown={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                            }}
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              removeBpVideoAt(expanded ? idx : 0);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                removeBpVideoAt(expanded ? idx : 0);
                              }
                            }}
                            style={{
                              position: 'absolute',
                              top: -6,
                              right: -6,
                              width: 18,
                              height: 18,
                              borderRadius: 999,
                              background: 'rgba(0,0,0,0.55)',
                              color: 'rgba(255,255,255,0.9)',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                            }}
                            aria-label="delete"
                          >
                            <CloseOutlined style={{ fontSize: 10, lineHeight: 1 }} />
                          </span>
                        </div>
                      ))}
                    </div>

                    {!shouldHideAdd ? (
                      <Button
                        type="text"
                        onClick={() => openAssetSelector('video')}
                        icon={
                          <span
                            style={{
                              width: '1.25rem',
                              height: '1.25rem',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              lineHeight: 1,
                              color: token.colorText,
                            }}
                            aria-hidden
                          >
                            <i
                              className="iconfont icon-video"
                              style={{ fontSize: '1.25rem', lineHeight: 1 }}
                              aria-hidden
                            />
                          </span>
                        }
                        style={{
                          width: '2.5rem',
                          height: '3.5rem',
                          padding: 0,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          borderRadius: 8,
                          border: `1px dashed ${token.colorBorder}`,
                          background: token.colorFillQuaternary,
                        }}
                      />
                    ) : null}
                  </div>
                );
              })() : null}

              {firstFrameKey || lastFrameKey ? (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    height: '3.5rem',
                    gap: 4,
                    flexShrink: 0,
                    paddingInline: 6,
                  }}
                >
                  {firstFrameKey ? (
                    <div
                      style={{
                        width: '3rem',
                        height: '3.5rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'visible',
                      }}
                    >
                      <Button
                        className="homeFrameSlotButton"
                        type="text"
                        onClick={() => openAssetSelector('image', firstFrameKey)}
                        style={{
                          width: '2.5rem',
                          height: '3.5rem',
                          padding: 0,
                          display: 'inline-flex',
                          transform: 'rotate(-10deg)',
                          transformOrigin: 'bottom center',
                          borderRadius: 8,
                          border: `1px dashed ${token.colorBorder}`,
                          background: token.colorFillQuaternary,
                          overflow: 'visible',
                        }}
                      >
                        {(assetSelections[firstFrameKey] ?? [])[0] ? (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              position: 'relative',
                              overflow: 'visible',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: 8,
                                overflow: 'hidden',
                                border: `1px solid ${token.colorBorderSecondary}`,
                                background: token.colorFillQuaternary,
                              }}
                            >
                            <img
                              src={(assetSelections[firstFrameKey] ?? [])[0].url}
                              alt=""
                              draggable={false}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                              referrerPolicy="no-referrer"
                            />
                            </div>
                            <span
                              role="button"
                              tabIndex={0}
                              data-asset-preview-delete="1"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setAssetSelections((prev) => ({ ...prev, [firstFrameKey]: [] }));
                              }}
                              style={{
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                width: 18,
                                height: 18,
                                borderRadius: 999,
                                background: 'rgba(0,0,0,0.55)',
                                color: 'rgba(255,255,255,0.9)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                              aria-label="delete"
                            >
                              <CloseOutlined style={{ fontSize: 10, lineHeight: 1 }} />
                            </span>
                          </div>
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              padding: '6px 0px',
                              minHeight: 0,
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              aria-hidden
                            >
                              <i
                                className="iconfont icon-plus"
                                style={{
                                  fontSize: '0.875rem',
                                  lineHeight: 1,
                                  display: 'block',
                                }}
                                aria-hidden
                              />
                            </div>
                            <div
                              style={{
                                textAlign: 'center',
                                fontSize: '0.625rem',
                                lineHeight: 1,
                                color: token.colorTextSecondary,
                                paddingInline: 2,
                                whiteSpace: 'pre-line',
                                wordBreak: 'normal',
                                overflowWrap: 'normal',
                              }}
                            >
                              {firstFrameName ??
                                intl.formatMessage({ id: 'ai.integration.firstFrame' })}
                            </div>
                          </div>
                        )}
                      </Button>
                    </div>
                  ) : null}

                  {firstFrameKey && lastFrameKey ? (
                    <div
                      style={{
                        height: '3.5rem',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                      aria-hidden
                    >
                      <i
                        className="iconfont icon-arrow_left_right"
                        style={{
                          fontSize: '0.75rem',
                          lineHeight: 1,
                          color: 'hsl(0 0% 63.9%)',
                        }}
                      />
                    </div>
                  ) : null}

                  {lastFrameKey ? (
                    <div
                      style={{
                        width: '3rem',
                        height: '3.5rem',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'visible',
                      }}
                    >
                      <Button
                        className="homeFrameSlotButton"
                        type="text"
                        onClick={() => openAssetSelector('image', lastFrameKey)}
                        style={{
                          width: '2.5rem',
                          height: '3.5rem',
                          padding: 0,
                          display: 'inline-flex',
                          transform: 'rotate(10deg)',
                          transformOrigin: 'bottom center',
                          borderRadius: 8,
                          border: `1px dashed ${token.colorBorder}`,
                          background: token.colorFillQuaternary,
                          overflow: 'visible',
                        }}
                      >
                        {(assetSelections[lastFrameKey] ?? [])[0] ? (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              position: 'relative',
                              overflow: 'visible',
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: 8,
                                overflow: 'hidden',
                                border: `1px solid ${token.colorBorderSecondary}`,
                                background: token.colorFillQuaternary,
                              }}
                            >
                            <img
                              src={(assetSelections[lastFrameKey] ?? [])[0].url}
                              alt=""
                              draggable={false}
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover',
                                display: 'block',
                              }}
                              referrerPolicy="no-referrer"
                            />
                            </div>
                            <span
                              role="button"
                              tabIndex={0}
                              data-asset-preview-delete="1"
                              onMouseDown={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                              }}
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setAssetSelections((prev) => ({ ...prev, [lastFrameKey]: [] }));
                              }}
                              style={{
                                position: 'absolute',
                                top: -6,
                                right: -6,
                                width: 18,
                                height: 18,
                                borderRadius: 999,
                                background: 'rgba(0,0,0,0.55)',
                                color: 'rgba(255,255,255,0.9)',
                                display: 'inline-flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                              }}
                              aria-label="delete"
                            >
                              <CloseOutlined style={{ fontSize: 10, lineHeight: 1 }} />
                            </span>
                          </div>
                        ) : (
                          <div
                            style={{
                              width: '100%',
                              height: '100%',
                              display: 'flex',
                              flexDirection: 'column',
                              justifyContent: 'space-between',
                              padding: '6px 0px',
                              minHeight: 0,
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                minHeight: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                              aria-hidden
                            >
                              <i
                                className="iconfont icon-plus"
                                style={{
                                  fontSize: '0.875rem',
                                  lineHeight: 1,
                                  display: 'block',
                                }}
                                aria-hidden
                              />
                            </div>
                            <div
                              style={{
                                textAlign: 'center',
                                fontSize: '0.625rem',
                                lineHeight: 1,
                                color: token.colorTextSecondary,
                                paddingInline: 2,
                                whiteSpace: 'pre-line',
                                wordBreak: 'normal',
                                overflowWrap: 'normal',
                              }}
                            >
                              {lastFrameName ??
                                intl.formatMessage({ id: 'ai.integration.lastFrame' })}
                            </div>
                          </div>
                        )}
                      </Button>
                    </div>
                  ) : null}
                </div>
              ) : null}

              {getComposerPromptKey(paramSchema) ? (
                <Input.TextArea
                  className="homePromptTextArea"
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={
                    bpPromptTips ?? intl.formatMessage({ id: 'ai.integration.promptPlaceholder' })
                  }
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  variant="borderless"
                  style={{
                    background: 'transparent',
                    flex: 1,
                    minWidth: 0,
                    padding: '0.5rem 0rem',
                    resize: 'none',
                    color: token.colorText,
                    fontSize: '0.875rem',
                  }}
                />
              ) : null}
            </div>
          </div>

          <div style={{ display: 'flex', gap: 16, paddingTop: '0.5rem' }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12,
                  flexWrap: 'nowrap',
                  whiteSpace: 'nowrap',
                }}
              >
                <Space size={10} style={{ flex: 1, minWidth: 0 }}>
                  {/* 模型 */}
                  <Dropdown
                    open={modelDropdownOpen}
                    onOpenChange={setModelDropdownOpen}
                    trigger={['click']}
                    placement="bottomLeft"
                    align={{ points: ['tl', 'bl'], offset: [0, 4] }}
                    overlayStyle={{ minWidth: 360 }}
                    overlayClassName="homeModelDropdownOverlay"
                    popupRender={() => (
                      <div className="homeModelDropdown" style={{ borderRadius: 12 }}>
                        {modelsLoading ? (
                          <div style={{ padding: 12, color: token.colorTextSecondary }}>加载中...</div>
                        ) : models.length === 0 ? (
                          <div style={{ padding: 12, color: token.colorTextSecondary }}>暂无模型</div>
                        ) : null}
                        {models.map((m) => {
                          const active = m.id === selectedModelId;
                          return (
                            <div
                              key={m.id}
                              className={`homeModelOption${active ? ' homeModelOptionActive' : ''}`}
                              role="button"
                              tabIndex={0}
                              onClick={() => {
                                setSelectedModelId(m.id);
                                setModelDropdownOpen(false);
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  setSelectedModelId(m.id);
                                  setModelDropdownOpen(false);
                                }
                              }}
                            >
                              <div className="homeModelOptionRow">
                                <div className="homeModelLabel">{m.modelName}</div>
                                <div className="homeModelCost">
                                  <i className="iconfont icon-sparkles" aria-hidden />
                                  {m.modelCost}+
                                </div>
                              </div>
                              <div className="homeModelOptionRow">
                                <div className="homeModelDesc">{m.modelDesc}</div>
                                <div className="homeModelUsage">{m.modelUsage}</div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  >
                    <Button
                      className="homeModelTrigger"
                      size="small"
                      type="default"
                      style={{
                        height: 28,
                        paddingInline: 12,
                        borderRadius: 999,
                        background: token.colorFillTertiary,
                        borderColor: 'transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                      }}
                      icon={
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: token.colorText,
                          }}
                          aria-hidden
                        >
                          <i className="iconfont icon-package" aria-hidden />
                        </span>
                      }
                    >
                      {modelsLoading
                        ? '加载中...'
                        : (selectedModel?.modelName ?? intl.formatMessage({ id: 'ai.integration.modelPlaceholder' }))}
                    </Button>
                  </Dropdown>

                  {/* 参数 */}
                  <Dropdown
                    open={paramPanelOpen}
                    onOpenChange={setParamPanelOpen}
                    trigger={['click']}
                    placement="bottomLeft"
                    overlayClassName="homeParamDropdownOverlay"
                    popupRender={() => (
                      <div
                        className="homeParamDropdown"
                        style={{
                          minWidth: 'max-content',
                          background: token.colorBgLayout,
                          borderRadius: 16,
                          border: `1px solid ${token.colorBorderSecondary}`,
                        }}
                      >
                        {paramLoading ? (
                          <div style={{ padding: 12 }}>加载中...</div>
                        ) : (
                          <div className="homeParamBody">
                            {paramSchema
                              .filter((c) => isPanelSpecCandidate(c, paramSchema))
                              .map((c) => {
                                const min = c.componentMinLimit;
                                const max = c.componentMaxLimit;
                                const value = paramValues[c.componentKey];
                                const setValue = (v: unknown) =>
                                  setParamValues((prev) => ({
                                    ...prev,
                                    [c.componentKey]: v,
                                  }));

                                return (
                                  <div key={c.componentKey} className="homeParamItem">
                                    <div className="homeParamLabel">
                                      {c.componentName}
                                      {c.componentRequired === 1 ? (
                                        <span className="homeParamRequiredStar">*</span>
                                      ) : null}
                                    </div>

                                    {c.componentType === 1 ? (
                                      <Input
                                        value={typeof value === 'string' ? value : ''}
                                        onChange={(e) => setValue(e.target.value)}
                                        placeholder={c.componentTips}
                                      />
                                    ) : null}

                                    {c.componentType === 2 ? (
                                      <Select
                                        value={
                                          typeof value === 'string'
                                            ? value
                                            : c.componentDefaultValue
                                        }
                                        onChange={(v) => setValue(v)}
                                        options={(c.componentPreset ?? []).map((p) => ({
                                          label: p.label,
                                          value: p.value,
                                        }))}
                                      />
                                    ) : null}

                                    {c.componentType === 3 ? (
                                      <Input.TextArea
                                        value={typeof value === 'string' ? value : ''}
                                        onChange={(e) => setValue(e.target.value)}
                                        placeholder={c.componentTips}
                                        autoSize={{ minRows: 3, maxRows: 6 }}
                                      />
                                    ) : null}

                                    {c.componentType === 4 || c.componentType === 5 ? (
                                      <Input
                                        value={typeof value === 'string' ? value : ''}
                                        onChange={(e) => setValue(e.target.value)}
                                        placeholder={
                                          c.componentTips ??
                                          `请输入${c.componentType === 4 ? '图片' : '视频'} URL（逗号分隔）`
                                        }
                                      />
                                    ) : null}

                                    {c.componentType === 6 ? (
                                      <div className="homeParamTags">
                                        {(c.componentPreset ?? []).map((p) => {
                                          const current = coerceListValue(value);
                                          const checked = current.includes(p.value);
                                          return (
                                            <Tag.CheckableTag
                                              key={p.value}
                                              checked={checked}
                                              onChange={(next) => {
                                                let nextList = current.slice();
                                                const minN =
                                                  typeof min === 'number' ? min : 0;
                                                const maxN =
                                                  typeof max === 'number'
                                                    ? max
                                                    : nextList.length || 1;
                                                if (next) {
                                                  if (maxN <= 1) {
                                                    nextList = [p.value];
                                                  } else if (!nextList.includes(p.value)) {
                                                    nextList.push(p.value);
                                                  }
                                                } else {
                                                  const removed = nextList.filter((x) => x !== p.value);
                                                  if (
                                                    removed.length >= minN ||
                                                    removed.length === nextList.length
                                                  ) {
                                                    nextList = removed;
                                                  }
                                                }
                                                if (nextList.length > maxN) {
                                                  nextList = nextList.slice(0, maxN);
                                                }
                                                setValue(nextList);
                                              }}
                                              className="homeParamTag"
                                            >
                                              {p.label}
                                            </Tag.CheckableTag>
                                          );
                                        })}
                                      </div>
                                    ) : null}

                                    {c.componentType === 7 ? (
                                      <div
                                        style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 12,
                                        }}
                                      >
                                        <Slider
                                          min={typeof min === 'number' ? min : 1}
                                          max={typeof max === 'number' ? max : 60}
                                          value={Number(value ?? c.componentDefaultValue ?? 0)}
                                          onChange={(v) => setValue(String(v))}
                                          tooltip={{ open: false }}
                                          style={{ flex: 1 }}
                                        />
                                        <Input
                                          value={
                                            typeof value === 'string'
                                              ? value
                                              : String(value ?? '')
                                          }
                                          onChange={(e) => setValue(e.target.value)}
                                          suffix="s"
                                          style={{ width: 60 }}
                                        />
                                      </div>
                                    ) : null}
                                  </div>
                                );
                              })}
                          </div>
                        )}
                      </div>
                    )}
                  >
                    <Button
                      className="homeModelTrigger"
                      size="small"
                      type="default"
                      style={{
                        height: 28,
                        paddingInline: 12,
                        borderRadius: 999,
                        background: token.colorFillTertiary,
                        borderColor: 'transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                      }}
                      icon={
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: token.colorText,
                          }}
                          aria-hidden
                        >
                          <i className="iconfont icon-Settings2" aria-hidden />
                        </span>
                      }
                    >
                      {paramPanelSpecSummary ||
                        intl.formatMessage({ id: 'ai.integration.panelSpec' })}
                    </Button>
                  </Dropdown>

                  {/* 生成数量 */}
                  <Dropdown
                    open={generateCountOpen}
                    onOpenChange={setGenerateCountOpen}
                    trigger={['click']}
                    placement="bottomLeft"
                    align={{ points: ['tl', 'bl'], offset: [0, 4] }}
                    popupRender={() => (
                      <div
                        style={{
                          background: token.colorBgLayout,
                          borderRadius: 12,
                          border: `1px solid ${token.colorBorderSecondary}`,
                          padding: 8,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 4,
                        }}
                      >
                        {(Array.from({ length: 6 }, (_, i) => (i + 1) as 1 | 2 | 3 | 4 | 5 | 6)).map(
                          (v) => (
                            <Button
                              key={v}
                              type="text"
                              style={{
                                justifyContent: 'flex-start',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 8,
                                borderRadius: 10,
                              }}
                              onClick={() => {
                                setGenerateCount(v);
                                setGenerateCountOpen(false);
                              }}
                            >
                              {intl.formatMessage(
                                { id: 'ai.integration.generate.option' },
                                { count: v },
                              )}
                            </Button>
                          ),
                        )}
                      </div>
                    )}
                  >
                    <Button
                      className="homeModelTrigger"
                      size="small"
                      type="default"
                      style={{
                        height: 28,
                        paddingInline: 12,
                        borderRadius: 999,
                        background: token.colorFillTertiary,
                        borderColor: 'transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                      }}
                      icon={
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: token.colorText,
                          }}
                          aria-hidden
                        >
                          <i className="iconfont icon-gallery-horizontal-end" aria-hidden />
                        </span>
                      }
                    >
                      {intl.formatMessage(
                        { id: 'ai.integration.generate.count' },
                        { count: generateCount },
                      )}
                    </Button>
                  </Dropdown>
                </Space>

                <Space size={10} align="center" style={{ flex: 'none' }}>
                  <Typography.Text type="secondary">
                    {prompt.length}/8000
                  </Typography.Text>
                  <Tooltip
                    title={intl.formatMessage(
                      { id: 'ai.integration.generate.costTooltip' },
                      {
                        points: resolveModelCost(selectedModel?.modelCost) * generateCount,
                      },
                    )}
                  >
                    <Button
                      className="homeGenerateButton"
                      size="small"
                      type="primary"
                      loading={submitting}
                      disabled={submitting || paramLoading || modelsLoading}
                      onClick={handleSubmitCell}
                      style={{
                        height: 28,
                        paddingInline: 12,
                        borderRadius: 999,
                        borderColor: 'transparent',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 12,
                      }}
                      icon={
                        <span
                          style={{
                            width: 16,
                            height: 16,
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }}
                          aria-hidden
                        >
                          <i
                            className="iconfont icon-Sparkle"
                            style={{ color: 'rgb(16 185 129 / 1)' }}
                            aria-hidden
                          />
                        </span>
                      }
                    >
                      {resolveModelCost(selectedModel?.modelCost)}
                    </Button>
                  </Tooltip>
                </Space>
              </div>
            </div>
          </div>
            </div>
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default AIIntegration;

